import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Camera, Loader2, ShieldCheck, Upload } from "lucide-react";
import { toast } from "sonner";

export default function AgeVerifyModal({ open, onOpenChange, onVerified }) {
  const [selfie, setSelfie] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  function reset() {
    setSelfie("");
    setBusy(false);
  }

  function pick() {
    fileRef.current?.click();
  }

  function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) return toast.error("Photo too large (max 6MB).");
    const r = new FileReader();
    r.onload = () => setSelfie(r.result);
    r.readAsDataURL(file);
  }

  async function submit() {
    if (!selfie) return toast.error("Please upload a selfie first.");
    setBusy(true);
    try {
      const { data } = await api.post("/verify/age", { selfie_base64: selfie });
      if (data.verified) {
        toast.success(data.preview ? "Preview mode — verification bypassed." : "Age verified. NSFW unlocked.");
        onVerified && onVerified(data.user);
        onOpenChange(false);
        reset();
      } else {
        toast.error(data.message || "Verification failed.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="glass-strong border-white/10 max-w-md text-white" data-testid="age-verify-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <ShieldCheck size={20} className="text-violet-400" /> Age verification
          </DialogTitle>
        </DialogHeader>
        <div className="mt-2 space-y-4">
          <p className="text-sm text-neutral-400 leading-relaxed">
            To unlock NSFW content we do a quick, AI-based age check. Upload a clear selfie of yourself in good lighting.
            Your photo is used only for age estimation and not stored.
          </p>

          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
            {selfie ? (
              <div>
                <img src={selfie} alt="selfie preview" className="mx-auto max-h-64 rounded-xl object-cover" />
                <button
                  data-testid="age-verify-repick"
                  onClick={pick}
                  className="btn-pill btn-ghost mt-3 w-full text-xs"
                >
                  Choose a different photo
                </button>
              </div>
            ) : (
              <button
                data-testid="age-verify-pick"
                onClick={pick}
                className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 py-10 text-neutral-400 hover:bg-white/[0.02]"
              >
                <Camera size={24} />
                <span>Tap to upload selfie</span>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" capture="user" onChange={onFile} className="hidden" data-testid="age-verify-file-input" />
          </div>

          <button data-testid="age-verify-submit" onClick={submit} disabled={busy || !selfie} className="btn-pill btn-primary w-full py-3 disabled:opacity-50">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            {busy ? "Verifying…" : "Verify age"}
          </button>
          <p className="text-center text-[11px] text-neutral-600">
            This is a lightweight AI age estimation. For a stricter check we'll swap in a full KYC provider before launch.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
